import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Save, CheckCircle2, Trash2, ExternalLink, X, Loader2, ListChecks, Mail, Gauge, Compass, KeyRound, ShieldCheck, ShieldAlert, PauseCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { toast } from '@/components/ui/sonner';
import {
  adminListBlueprints,
  adminGetBlueprint,
  adminUpdateBlueprint,
  adminRegenerateBlueprint,
  adminDeleteBlueprint,
  adminRegenerateBlueprintSection,
  adminResendBlueprintEmail,
  adminGetBYOKStatus,
  adminSaveBYOKKey,
  adminRemoveBYOKKey,
  adminSetActiveBYOKProvider,
  adminGetResearchKeyStatus,
  adminSaveResearchKey,
  adminRemoveResearchKey,
  adminGetAIUsage,
  adminScoutFromBlueprint,
} from '@/lib/api';
import { ADMIN_INTELLIGENCE, SHOOT_MODE, BYOK_SETTINGS, RESEARCH_KEY_SETTINGS, AI_USAGE, SCOUT_ENTRY } from '@/constants/testIds';
import { AI_FEATURES_ENABLED } from '@/lib/featureFlags';

const SECTIONS = [
  { value: 'creative_direction', label: 'Creative direction' },
  { value: 'shot_list', label: 'Shot list' },
  { value: 'poses', label: 'Poses' },
  { value: 'lighting', label: 'Lighting plan' },
  { value: 'locations', label: 'Locations' },
  { value: 'composition_notes', label: 'Composition notes' },
  { value: 'style_tips', label: 'Style tips' },
  { value: 'timeline', label: 'Timeline' },
  { value: 'deliverables', label: 'Deliverables' },
];

const EMAIL_STYLE = {
  sent: 'text-[hsl(152,46%,65%)] border-[hsl(152,46%,35%)]/40',
  skipped: 'text-[hsl(var(--lxra-muted))] border-[hsl(var(--lxra-border))]',
  failed: 'text-[hsl(8,55%,68%)] border-[hsl(8,55%,45%)]/40',
};

const STATUS_STYLE = {
  generating: 'bg-[#141416] text-[hsl(var(--lxra-muted))] border-[hsl(var(--lxra-border))]',
  ready: 'bg-[hsl(var(--lxra-gold))]/15 text-[hsl(var(--lxra-gold-hover))] border-[hsl(var(--lxra-gold))]/30',
  reviewed: 'bg-[hsl(152,46%,25%)]/30 text-[hsl(152,46%,65%)] border-[hsl(152,46%,35%)]/40',
  failed: 'bg-[hsl(8,55%,40%)]/20 text-[hsl(8,55%,68%)] border-[hsl(8,55%,45%)]/40',
};

const StatusBadge = ({ status }) => (
  <Badge className={`border capitalize hover:bg-transparent ${STATUS_STYLE[status] || STATUS_STYLE.generating}`} data-testid={`blueprint-status-${status}`}>
    {status === 'generating' && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
    {status}
  </Badge>
);

const PROVIDER_STYLE = {
  openai: 'border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))]',
  anthropic: 'border-sky-500/40 text-sky-400',
  gemini: 'border-violet-400/40 text-violet-300',
};

const ModelBadge = ({ provider, model }) => {
  if (!model) {
    return <span className="text-xs text-[hsl(var(--lxra-muted))]">—</span>;
  }
  return (
    <Badge
      variant="outline"
      title={`Generated with ${provider}/${model}`}
      className={`text-[10px] font-normal whitespace-nowrap ${PROVIDER_STYLE[provider] || 'border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))]'}`}
    >
      {provider}/{model}
    </Badge>
  );
};

const fieldClass = 'mt-1.5 bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] focus-visible:ring-[hsl(var(--lxra-gold))]';
const labelClass = 'text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]';
const linesToList = (s) => s.split('\n').map((l) => l.trim()).filter(Boolean);

function BlueprintEditor({ id, onClose, onChanged }) {
  const [bp, setBp] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [section, setSection] = useState('poses');

  const load = useCallback(async () => {
    const data = await adminGetBlueprint(id);
    setBp(data);
    const c = data.content || {};
    setDraft({
      title: c.title || '',
      tagline: c.tagline || '',
      creative_direction: c.creative_direction || '',
      composition_notes: (c.composition_notes || []).join('\n'),
      style_tips: (c.style_tips || []).join('\n'),
      photographer_notes: c.photographer_notes || '',
    });
    return data;
  }, [id]);

  useEffect(() => {
    load().catch(() => toast.error('Could not load blueprint.'));
  }, [load]);

  // Poll while regenerating (full or single section).
  useEffect(() => {
    if (bp?.status !== 'generating' && !bp?.section_generating) return undefined;
    const t = setTimeout(
      () => load().then((d) => {
        if (d.status !== 'generating' && !d.section_generating) {
          onChanged();
          if (bp?.section_generating && !d.error) toast.success(`${bp.section_generating.replace('_', ' ')} regenerated.`);
        }
      }),
      3000,
    );
    return () => clearTimeout(t);
  }, [bp, load, onChanged]);

  const regenerateSection = async () => {
    setBusy(true);
    try {
      const updated = await adminRegenerateBlueprintSection(id, section);
      setBp(updated);
      toast.success(`Regenerating ${section.replace('_', ' ')}... other sections stay as they are.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not regenerate section.');
    } finally {
      setBusy(false);
    }
  };

  const resendEmail = async () => {
    setBusy(true);
    try {
      const res = await adminResendBlueprintEmail(id);
      if (res.status === 'sent') toast.success(res.message);
      else if (res.status === 'skipped') toast.warning(res.message);
      else toast.error(res.message);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send email.');
    } finally {
      setBusy(false);
    }
  };

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const updated = await adminUpdateBlueprint(id, {
        content: {
          title: draft.title,
          tagline: draft.tagline,
          creative_direction: draft.creative_direction,
          composition_notes: linesToList(draft.composition_notes),
          style_tips: linesToList(draft.style_tips),
          photographer_notes: draft.photographer_notes,
        },
        ...extra,
      });
      setBp(updated);
      onChanged();
      toast.success(extra.status === 'reviewed' ? 'Blueprint marked as reviewed.' : 'Blueprint saved.');
    } catch {
      toast.error('Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const updated = await adminRegenerateBlueprint(id);
      setBp(updated);
      onChanged();
      toast.success('Regenerating blueprint...');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not regenerate.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this blueprint? The lead will be kept.')) return;
    setBusy(true);
    try {
      await adminDeleteBlueprint(id);
      toast.success('Blueprint deleted.');
      onChanged();
      onClose();
    } catch {
      toast.error('Could not delete blueprint.');
      setBusy(false);
    }
  };

  if (!bp || !draft) return <Skeleton className="h-96 rounded-2xl bg-[#141416]" />;

  const req = bp.request || {};
  const disabled = bp.status === 'generating' || Boolean(bp.section_generating) || saving || busy;

  return (
    <div className="rounded-2xl border border-[hsl(var(--lxra-gold))]/30 bg-[#0F0F10] p-6 space-y-6" data-testid={ADMIN_INTELLIGENCE.detail}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <StatusBadge status={bp.status} />
            <ModelBadge provider={bp.provider} model={bp.model} />
            {bp.generation_ms && <span className="text-xs text-[hsl(var(--lxra-muted))]">{(bp.generation_ms / 1000).toFixed(1)}s to generate</span>}
          </div>
          <h3 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-2">
            {req.name} — {req.occasion}
          </h3>
          <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">
            {req.email} {req.phone && `· ${req.phone}`} · {req.people_count} in frame · {req.time_of_day} · {req.session_date || 'date flexible'}
          </p>
          {req.location_preference && <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">Location: {req.location_preference}</p>}
          {req.must_have_shots && <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">Must-haves: {req.must_have_shots}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ScoutButton blueprint={bp} />
          <Link
            to={`/admin/shoot/${bp.id}`}
            data-testid={SHOOT_MODE.editorOpen}
            className="inline-flex items-center h-9 px-3 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.08em] font-semibold transition-colors"
          >
            <ListChecks className="h-3.5 w-3.5 mr-1.5" /> Shoot Mode
          </Link>
          <Link
            to={`/plan/${bp.id}`}
            target="_blank"
            data-testid={ADMIN_INTELLIGENCE.viewClientLink}
            className="inline-flex items-center h-9 px-3 rounded-lg border border-[hsl(var(--lxra-border))] text-xs uppercase tracking-[0.08em] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Client View
          </Link>
          <button type="button" onClick={onClose} data-testid={ADMIN_INTELLIGENCE.closeButton} className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] hover:border-[hsl(var(--lxra-gold))] transition-colors" aria-label="Close editor">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {bp.error && (
        <p className="rounded-xl border border-[hsl(8,55%,45%)]/40 bg-[hsl(8,55%,40%)]/10 p-3 text-xs text-[hsl(8,55%,70%)]">{bp.error}</p>
      )}

      {/* Client delivery status */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[hsl(var(--lxra-border))] bg-[#141416] px-4 py-3">
        <Mail className="h-4 w-4 text-[hsl(var(--lxra-gold))]" />
        <span className="text-xs text-[hsl(var(--lxra-muted))]">Client email</span>
        <span
          className={`rounded-full border px-2.5 py-0.5 text-[11px] uppercase tracking-[0.1em] ${EMAIL_STYLE[bp.email_status] || EMAIL_STYLE.skipped}`}
          data-testid={SHOOT_MODE.emailStatus}
        >
          {bp.email_status || 'not sent'}
        </span>
        {bp.email_status === 'skipped' && (
          <span className="text-[11px] text-[hsl(var(--lxra-muted))]">SendGrid key not configured — add it to backend/.env to enable delivery.</span>
        )}
        {bp.email_last_attempt_at && (
          <span className="text-[11px] text-[hsl(var(--lxra-muted))]">Last attempt {new Date(bp.email_last_attempt_at).toLocaleString()}</span>
        )}
        <button
          type="button"
          onClick={resendEmail}
          disabled={disabled || !bp.content}
          data-testid={SHOOT_MODE.resendEmail}
          className="ml-auto inline-flex items-center h-8 px-3 rounded-lg border border-[hsl(var(--lxra-border))] text-xs uppercase tracking-[0.08em] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors disabled:opacity-50"
        >
          {bp.email_status ? 'Resend' : 'Send'} blueprint email
        </button>
      </div>

      {bp.status === 'generating' ? (
        <div className="flex items-center gap-3 text-sm text-[hsl(var(--lxra-muted))] py-6">
          <Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--lxra-gold))]" /> LXRA Intelligence is composing this blueprint...
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid={ADMIN_INTELLIGENCE.editor}>
          <div className="space-y-4">
            <div>
              <Label className={labelClass}>Title</Label>
              <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className={fieldClass} data-testid="admin-blueprint-title-input" />
            </div>
            <div>
              <Label className={labelClass}>Tagline</Label>
              <Input value={draft.tagline} onChange={(e) => setDraft({ ...draft, tagline: e.target.value })} className={fieldClass} data-testid="admin-blueprint-tagline-input" />
            </div>
            <div>
              <Label className={labelClass}>Creative Direction (client-facing)</Label>
              <Textarea rows={9} value={draft.creative_direction} onChange={(e) => setDraft({ ...draft, creative_direction: e.target.value })} className={`${fieldClass} resize-y`} data-testid="admin-blueprint-direction-textarea" />
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <Label className={labelClass}>Composition Notes (one per line)</Label>
              <Textarea rows={4} value={draft.composition_notes} onChange={(e) => setDraft({ ...draft, composition_notes: e.target.value })} className={`${fieldClass} resize-y`} />
            </div>
            <div>
              <Label className={labelClass}>Style Tips (one per line)</Label>
              <Textarea rows={4} value={draft.style_tips} onChange={(e) => setDraft({ ...draft, style_tips: e.target.value })} className={`${fieldClass} resize-y`} />
            </div>
            <div>
              <Label className={`${labelClass} text-[hsl(var(--lxra-gold))]`}>Photographer Notes (internal only)</Label>
              <Textarea rows={4} value={draft.photographer_notes} onChange={(e) => setDraft({ ...draft, photographer_notes: e.target.value })} className={`${fieldClass} resize-y border-[hsl(var(--lxra-gold))]/30`} data-testid="admin-blueprint-notes-textarea" />
            </div>
          </div>
        </div>
      )}

      {bp.content && bp.status !== 'generating' && (
        <p className="text-xs text-[hsl(var(--lxra-muted))]">
          {bp.content.shot_list?.length || 0} scenes · {bp.content.shot_list?.reduce((n, s) => n + (s.shots?.length || 0), 0) || 0} shots · {bp.content.poses?.length || 0} poses · {bp.content.locations?.length || 0} locations — full detail in the client view.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[hsl(var(--lxra-border))]">
        <button type="button" onClick={() => save()} disabled={disabled} data-testid={ADMIN_INTELLIGENCE.saveButton} className="inline-flex items-center h-10 px-4 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] transition-colors text-xs uppercase tracking-[0.08em] font-semibold disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save Edits
        </button>
        <button type="button" onClick={() => save({ status: 'reviewed' })} disabled={disabled} data-testid={ADMIN_INTELLIGENCE.reviewButton} className="inline-flex items-center h-10 px-4 rounded-xl border border-[hsl(var(--lxra-gold))]/50 text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors text-xs uppercase tracking-[0.08em] font-medium disabled:opacity-50">
          <CheckCircle2 className="h-4 w-4 mr-2" /> Save & Mark Reviewed
        </button>
        <div className="inline-flex items-stretch rounded-xl border border-[hsl(var(--lxra-border))] overflow-hidden">
          <Select value={section} onValueChange={setSection} disabled={disabled}>
            <SelectTrigger className="h-10 w-44 rounded-none border-0 border-r border-[hsl(var(--lxra-border))] bg-[#141416] text-xs text-[hsl(var(--lxra-fg))] focus:ring-0" data-testid={SHOOT_MODE.sectionSelect}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
              {SECTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button type="button" onClick={regenerateSection} disabled={disabled || !bp.content} data-testid={SHOOT_MODE.sectionRegenerate} className="inline-flex items-center h-10 px-4 bg-transparent text-[hsl(var(--lxra-fg))] hover:text-[hsl(var(--lxra-gold))] transition-colors text-xs uppercase tracking-[0.08em] font-medium disabled:opacity-50">
            {bp.section_generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {bp.section_generating ? `Redoing ${bp.section_generating.replace('_', ' ')}` : 'Redo section'}
          </button>
        </div>
        <button type="button" onClick={regenerate} disabled={disabled} data-testid={ADMIN_INTELLIGENCE.regenerateButton} className="inline-flex items-center h-10 px-4 rounded-xl border border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors text-xs uppercase tracking-[0.08em] font-medium disabled:opacity-50">
          <RefreshCw className="h-4 w-4 mr-2" /> Regenerate all
        </button>
        <button type="button" onClick={remove} disabled={saving || busy} data-testid={ADMIN_INTELLIGENCE.deleteButton} className="ml-auto inline-flex items-center h-10 px-3 rounded-xl text-[hsl(var(--lxra-muted))] hover:text-[hsl(8,55%,68%)] transition-colors text-xs uppercase tracking-[0.08em] disabled:opacity-50">
          <Trash2 className="h-4 w-4 mr-2" /> Delete
        </button>
      </div>
    </div>
  );
}

const BYOK_PLACEHOLDERS = { openai: 'sk-...', anthropic: 'sk-ant-...', gemini: 'AIzaSy...' };
const BYOK_SHORT_LABEL = { openai: 'OpenAI', anthropic: 'Claude', gemini: 'Gemini' };

function BYOKSettingsCard() {
  const [status, setStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('openai');
  const [forms, setForms] = useState({});
  const [savingProvider, setSavingProvider] = useState(null);
  const [removingProvider, setRemovingProvider] = useState(null);
  const [activatingProvider, setActivatingProvider] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await adminGetBYOKStatus();
      setStatus(data);
      setForms((prev) => {
        const next = { ...prev };
        Object.entries(data.providers).forEach(([name, p]) => {
          next[name] = { apiKey: next[name]?.apiKey || '', model: p.model, imageModel: p.image_model || '' };
        });
        return next;
      });
      if (data.active_provider) setActiveTab(data.active_provider);
    } catch {
      setStatus(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateForm = (provider, patch) => setForms((prev) => ({ ...prev, [provider]: { ...prev[provider], ...patch } }));

  const handleSave = async (provider) => {
    const form = forms[provider] || {};
    if (!form.apiKey?.trim()) {
      toast.error('Paste an API key first.');
      return;
    }
    setSavingProvider(provider);
    try {
      const updated = await adminSaveBYOKKey(provider, form.apiKey.trim(), form.model, form.imageModel || undefined);
      setStatus(updated);
      updateForm(provider, { apiKey: '' });
      const justActivated = updated.active_provider === provider;
      toast.success(`${updated.providers[provider].label} key saved${justActivated ? ' — now the active AI provider.' : '.'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the key.');
    } finally {
      setSavingProvider(null);
    }
  };

  const handleRemove = async (provider) => {
    const label = status?.providers?.[provider]?.label || provider;
    if (!window.confirm(`Remove the ${label} key? If it's the active provider, every AI feature (Blueprints, Scout, Gear Library drafts, Culling/Color vision checks, cover images, Autonomous Blog Writer) will show "AI unavailable" until another provider is activated.`)) return;
    setRemovingProvider(provider);
    try {
      const updated = await adminRemoveBYOKKey(provider);
      setStatus(updated);
      toast.success(`${label} key removed.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove the key.');
    } finally {
      setRemovingProvider(null);
    }
  };

  const handleActivate = async (provider) => {
    setActivatingProvider(provider);
    try {
      const updated = await adminSetActiveBYOKProvider(provider);
      setStatus(updated);
      toast.success(`${updated.providers[provider].label} is now the active AI provider.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not activate this provider.');
    } finally {
      setActivatingProvider(null);
    }
  };

  if (status === null) return <Skeleton className="h-64 rounded-2xl bg-[#141416] w-full sm:w-[420px]" />;
  if (status === false) return null;

  const providers = ['openai', 'anthropic', 'gemini'];
  const activeInfo = status.active_provider ? status.providers[status.active_provider] : null;

  return (
    <div
      data-testid={BYOK_SETTINGS.card}
      className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-4 flex flex-col gap-3 w-full sm:w-[420px]"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))]">
          <KeyRound className="h-4 w-4 text-[hsl(var(--lxra-gold))]" />
          AI Provider (BYOK)
        </div>
        <Badge
          className={`border capitalize hover:bg-transparent text-[10px] ${status.configured ? 'bg-[hsl(152,46%,25%)]/30 text-[hsl(152,46%,65%)] border-[hsl(152,46%,35%)]/40' : 'bg-[hsl(8,55%,40%)]/20 text-[hsl(8,55%,68%)] border-[hsl(8,55%,45%)]/40'}`}
        >
          {status.configured ? <ShieldCheck className="h-3 w-3 mr-1" /> : <ShieldAlert className="h-3 w-3 mr-1" />}
          {status.configured ? `Active: ${activeInfo?.label}` : 'AI Unavailable'}
        </Badge>
      </div>
      <p data-testid={BYOK_SETTINGS.helpText} className="text-[11px] text-[hsl(var(--lxra-muted))]">
        Save a key for one or more providers, then pick which one is active. Every Intelligence feature — Blueprints, Scout, Gear Library drafts, Culling/Color vision checks, cover images, Autonomous Blog Writer — runs on the active provider and its own billing. No key active = "AI unavailable", with no silent fallback.
      </p>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 bg-[#141416] border border-[hsl(var(--lxra-border))] h-9">
          {providers.map((p) => (
            <TabsTrigger
              key={p}
              value={p}
              data-testid={BYOK_SETTINGS.providerTab(p)}
              className="text-[11px] data-[state=active]:bg-[hsl(var(--lxra-gold))] data-[state=active]:text-[#0A0A0A]"
            >
              {BYOK_SHORT_LABEL[p]}
              {status.active_provider === p && (
                <span data-testid={BYOK_SETTINGS.activeBadge(p)} className="ml-1.5 h-1.5 w-1.5 rounded-full bg-[hsl(152,46%,55%)] inline-block" />
              )}
            </TabsTrigger>
          ))}
        </TabsList>
        {providers.map((p) => {
          const info = status.providers[p];
          const form = forms[p] || {};
          const isActive = status.active_provider === p;
          const saving = savingProvider === p;
          const removing = removingProvider === p;
          const activating = activatingProvider === p;
          return (
            <TabsContent key={p} value={p} className="space-y-3 pt-3">
              <div className="flex items-center justify-between gap-2">
                <Badge
                  data-testid={BYOK_SETTINGS.statusBadge(p)}
                  className={`border hover:bg-transparent text-[10px] ${info.configured ? (isActive ? 'bg-[hsl(152,46%,25%)]/30 text-[hsl(152,46%,65%)] border-[hsl(152,46%,35%)]/40' : 'bg-[hsl(var(--lxra-gold))]/10 text-[hsl(var(--lxra-gold))] border-[hsl(var(--lxra-gold))]/30') : 'bg-transparent text-[hsl(var(--lxra-muted))] border-[hsl(var(--lxra-border))]'}`}
                >
                  {info.configured ? (isActive ? 'Active' : 'Connected, inactive') : 'Not connected'}
                </Badge>
                <div className="flex items-center gap-1.5">
                  <Badge data-testid={BYOK_SETTINGS.capabilityBadge(p, 'vision')} variant="outline" className="text-[9px] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))]">Vision</Badge>
                  <Badge
                    data-testid={BYOK_SETTINGS.capabilityBadge(p, 'image')}
                    variant="outline"
                    className={`text-[9px] border-[hsl(var(--lxra-border))] ${info.supports_image ? 'text-[hsl(var(--lxra-muted))]' : 'text-[hsl(var(--lxra-muted))]/40'}`}
                  >
                    {info.supports_image ? 'Image gen' : 'No image gen'}
                  </Badge>
                </div>
              </div>

              {info.configured && (
                <p className="text-[11px] text-[hsl(var(--lxra-muted))]">
                  Key <span className="text-[hsl(var(--lxra-fg))] font-mono">{info.masked_key}</span>
                </p>
              )}

              <div>
                <Label className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">API key</Label>
                <Input
                  type="password"
                  value={form.apiKey || ''}
                  onChange={(e) => updateForm(p, { apiKey: e.target.value })}
                  placeholder={BYOK_PLACEHOLDERS[p]}
                  data-testid={BYOK_SETTINGS.keyInput(p)}
                  className="mt-1 h-9 bg-[#141416] border-[hsl(var(--lxra-border))] text-xs text-[hsl(var(--lxra-fg))] focus-visible:ring-[hsl(var(--lxra-gold))]"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Model</Label>
                  <Select value={form.model} onValueChange={(v) => updateForm(p, { model: v })}>
                    <SelectTrigger data-testid={BYOK_SETTINGS.modelSelect(p)} className="mt-1 h-9 bg-[#141416] border-[hsl(var(--lxra-border))] text-xs text-[hsl(var(--lxra-fg))]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
                      {info.models.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {info.supports_image ? (
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Image model</Label>
                    <Select value={form.imageModel} onValueChange={(v) => updateForm(p, { imageModel: v })}>
                      <SelectTrigger data-testid={BYOK_SETTINGS.imageModelSelect(p)} className="mt-1 h-9 bg-[#141416] border-[hsl(var(--lxra-border))] text-xs text-[hsl(var(--lxra-fg))]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
                        {info.image_models.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="flex items-end">
                    <p className="text-[10px] text-[hsl(var(--lxra-muted))] pb-2">Claude has no image generation — cover images &amp; concept renders need OpenAI or Gemini active.</p>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  onClick={() => handleSave(p)}
                  disabled={saving || removing || activating}
                  data-testid={BYOK_SETTINGS.saveButton(p)}
                  className="h-9 px-3 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] disabled:opacity-50 text-xs uppercase tracking-[0.08em] font-semibold"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (info.configured ? 'Update key' : 'Save key')}
                </Button>
                {info.configured && !isActive && (
                  <Button
                    onClick={() => handleActivate(p)}
                    disabled={saving || removing || activating}
                    variant="outline"
                    data-testid={BYOK_SETTINGS.activateButton(p)}
                    className="h-9 px-3 rounded-lg border-[hsl(var(--lxra-gold))]/40 bg-transparent text-[hsl(var(--lxra-gold))] hover:bg-[hsl(var(--lxra-gold))]/10 disabled:opacity-50 text-xs uppercase tracking-[0.08em]"
                  >
                    {activating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Set active'}
                  </Button>
                )}
                {info.configured && (
                  <Button
                    onClick={() => handleRemove(p)}
                    disabled={saving || removing || activating}
                    variant="outline"
                    data-testid={BYOK_SETTINGS.removeButton(p)}
                    className="h-9 px-3 rounded-lg border-[hsl(var(--lxra-border))] bg-transparent text-[hsl(var(--lxra-muted))] hover:text-[hsl(8,55%,68%)] hover:border-[hsl(8,55%,45%)]/40 disabled:opacity-50 text-xs uppercase tracking-[0.08em]"
                  >
                    {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove</>}
                  </Button>
                )}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}

function ResearchKeyCard() {
  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('sonar-pro');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await adminGetResearchKeyStatus();
      setStatus(data);
      setModel(data.model || 'sonar-pro');
    } catch {
      setStatus(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!apiKey.trim()) {
      toast.error('Paste your Perplexity API key first.');
      return;
    }
    setSaving(true);
    try {
      const updated = await adminSaveResearchKey(apiKey.trim(), model);
      setStatus(updated);
      setApiKey('');
      toast.success('Perplexity key saved — Gear Library & Blog Writer drafts will now ground themselves in real web research.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the key.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove the Perplexity key? Gear Library and Blog Writer drafts will go back to generating from the model\'s own knowledge, without live web research.')) return;
    setRemoving(true);
    try {
      const updated = await adminRemoveResearchKey();
      setStatus(updated);
      toast.success('Perplexity key removed.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove the key.');
    } finally {
      setRemoving(false);
    }
  };

  if (status === null) return <Skeleton className="h-40 rounded-2xl bg-[#141416] w-full sm:w-80" />;
  if (status === false) return null;

  return (
    <div data-testid={RESEARCH_KEY_SETTINGS.card} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-4 flex flex-col gap-3 w-full sm:w-80">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))]">
          <Compass className="h-4 w-4 text-[hsl(var(--lxra-gold))]" />
          Research (Perplexity)
        </div>
        <Badge
          data-testid={RESEARCH_KEY_SETTINGS.statusBadge}
          className={`border hover:bg-transparent text-[10px] ${status.configured ? 'bg-[hsl(152,46%,25%)]/30 text-[hsl(152,46%,65%)] border-[hsl(152,46%,35%)]/40' : 'bg-transparent text-[hsl(var(--lxra-muted))] border-[hsl(var(--lxra-border))]'}`}
        >
          {status.configured ? 'Connected' : 'Not connected'}
        </Badge>
      </div>
      <p className="text-[11px] text-[hsl(var(--lxra-muted))]">
        Optional. Grounds Gear Library drafts and the Autonomous Blog Writer in real-time web search facts before writing — gear gets real specs, blog posts get current trends. Without it, drafts rely on the model's own training data.
      </p>
      {status.configured && (
        <p className="text-[11px] text-[hsl(var(--lxra-muted))]">
          Key <span className="text-[hsl(var(--lxra-fg))] font-mono">{status.masked_key}</span>
        </p>
      )}
      <div>
        <Label className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">API key</Label>
        <Input
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="pplx-..."
          data-testid={RESEARCH_KEY_SETTINGS.keyInput}
          className="mt-1 h-9 bg-[#141416] border-[hsl(var(--lxra-border))] text-xs text-[hsl(var(--lxra-fg))] focus-visible:ring-[hsl(var(--lxra-gold))]"
        />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Model</Label>
        <Select value={model} onValueChange={setModel}>
          <SelectTrigger data-testid={RESEARCH_KEY_SETTINGS.modelSelect} className="mt-1 h-9 bg-[#141416] border-[hsl(var(--lxra-border))] text-xs text-[hsl(var(--lxra-fg))]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
            {(status.models || ['sonar', 'sonar-pro', 'sonar-reasoning-pro']).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <Button
          onClick={handleSave}
          disabled={saving || removing}
          data-testid={RESEARCH_KEY_SETTINGS.saveButton}
          className="h-9 px-3 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] disabled:opacity-50 text-xs uppercase tracking-[0.08em] font-semibold"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (status.configured ? 'Update key' : 'Save key')}
        </Button>
        {status.configured && (
          <Button
            onClick={handleRemove}
            disabled={saving || removing}
            variant="outline"
            data-testid={RESEARCH_KEY_SETTINGS.removeButton}
            className="h-9 px-3 rounded-lg border-[hsl(var(--lxra-border))] bg-transparent text-[hsl(var(--lxra-muted))] hover:text-[hsl(8,55%,68%)] hover:border-[hsl(8,55%,45%)]/40 disabled:opacity-50 text-xs uppercase tracking-[0.08em]"
          >
            {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove</>}
          </Button>
        )}
      </div>
    </div>
  );
}

function ScoutButton({ blueprint }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const req = blueprint.request || {};
  const canScout = Boolean(req.location_preference && req.session_date);
  const run = async () => {
    setBusy(true);
    try {
      const doc = await adminScoutFromBlueprint(blueprint.id);
      toast.success('Scouting the location…');
      navigate(`/admin/scout/${doc.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start the scout.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={run}
      disabled={busy || !canScout}
      title={canScout ? 'Sun, light, positions and settings for this location and date' : 'Needs a location and a calendar date on the blueprint'}
      data-testid={SCOUT_ENTRY.blueprintButton}
      className="inline-flex items-center h-9 px-3 rounded-lg border border-[hsl(var(--lxra-gold))]/40 text-xs uppercase tracking-[0.08em] text-[hsl(var(--lxra-gold))] hover:bg-[hsl(var(--lxra-gold))]/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Compass className="h-3.5 w-3.5 mr-1.5" /> Scout Location</>}
    </button>
  );
}

const PROVIDER_DOT = {
  openai: 'bg-[hsl(var(--lxra-gold))]',
  anthropic: 'bg-sky-400',
  gemini: 'bg-violet-300',
};

function AIUsageCard({ refreshKey }) {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    adminGetAIUsage().then(setUsage).catch(() => setUsage(false));
  }, [refreshKey]);

  if (usage === null) return <Skeleton className="h-24 rounded-2xl bg-[#141416] w-full sm:w-72" />;
  if (usage === false) return null;

  return (
    <div
      data-testid={AI_USAGE.card}
      className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-4 flex flex-col gap-3 w-full sm:w-72"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))]">
          <Gauge className="h-4 w-4 text-[hsl(var(--lxra-gold))]" />
          Usage
          <span data-testid={AI_USAGE.monthLabel} className="normal-case tracking-normal">· {usage.month_label}</span>
        </div>
        <span data-testid={AI_USAGE.grandTotal} className="font-display text-2xl leading-none text-[hsl(var(--lxra-fg))]">
          {usage.grand_total}
        </span>
      </div>
      {usage.rows.length === 0 ? (
        <p data-testid={AI_USAGE.empty} className="text-[11px] text-[hsl(var(--lxra-muted))]">No blueprints generated yet this month.</p>
      ) : (
        <ul className="space-y-1.5">
          {usage.by_provider.map((p) => {
            const models = usage.rows.filter((r) => r.provider === p.provider);
            return (
              <li key={p.provider} data-testid={AI_USAGE.providerRow(p.provider)}>
                <div className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-2 text-[hsl(var(--lxra-fg))]">
                    <span className={`h-1.5 w-1.5 rounded-full ${PROVIDER_DOT[p.provider] || 'bg-[hsl(var(--lxra-muted))]'}`} />
                    {p.label}
                  </span>
                  <span className="text-[hsl(var(--lxra-fg))] font-medium">{p.total}</span>
                </div>
                {models.map((m) => (
                  <div
                    key={m.model}
                    data-testid={AI_USAGE.modelRow(m.provider, m.model)}
                    className="flex items-center justify-between pl-3.5 text-[11px] text-[hsl(var(--lxra-muted))]"
                    title={`${m.generate} new · ${m.regenerate} regenerated · ${m.section} section redos`}
                  >
                    <span className="truncate">{m.model}</span>
                    <span>{m.total}</span>
                  </div>
                ))}
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-[10px] text-[hsl(var(--lxra-muted))]">Counts every model call: new blueprints, full regenerations, and section redos.</p>
    </div>
  );
}

export const AdminIntelligenceTab = () => {
  const [blueprints, setBlueprints] = useState(null);
  const [selected, setSelected] = useState(null);

  const refresh = useCallback(() => adminListBlueprints().then(setBlueprints).catch(() => setBlueprints([])), []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Keep list fresh while any blueprint is generating.
  useEffect(() => {
    if (!blueprints?.some((b) => b.status === 'generating')) return undefined;
    const t = setTimeout(refresh, 4000);
    return () => clearTimeout(t);
  }, [blueprints, refresh]);

  return (
    <div className="space-y-6">
      {!AI_FEATURES_ENABLED && (
        <Alert data-testid="admin-intelligence-paused-banner" className="border-[hsl(var(--lxra-gold))]/30 bg-[hsl(var(--lxra-gold))]/10 text-[hsl(var(--lxra-fg))]">
          <PauseCircle className="h-4 w-4 text-[hsl(var(--lxra-gold))]" />
          <AlertTitle className="text-[hsl(var(--lxra-fg))]">AI features are paused platform-wide</AlertTitle>
          <AlertDescription className="text-[hsl(var(--lxra-muted))]">
            Blueprints, Scout, Culling, Color Intelligence, the Blog Writer, and Jev triage/screening are all temporarily
            switched off. Settings below are saved but won&apos;t generate anything until AI is re-enabled.
          </AlertDescription>
        </Alert>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))]">LXRA Intelligence</p>
          <h2 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-1">Creative Blueprints</h2>
          <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">Review, refine, and approve every blueprint before the client relies on it.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <AIUsageCard refreshKey={(blueprints || []).map((b) => b.updated_at || b.created_at).join('|')} />
          <BYOKSettingsCard />
          <ResearchKeyCard />
        </div>
      </div>

      {selected && <BlueprintEditor id={selected} onClose={() => setSelected(null)} onChanged={refresh} />}

      {!blueprints ? (
        <Skeleton className="h-64 rounded-2xl bg-[#141416]" />
      ) : (
        <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] overflow-hidden" data-testid={ADMIN_INTELLIGENCE.table}>
          <Table>
            <TableHeader>
              <TableRow className="border-[hsl(var(--lxra-border))] hover:bg-transparent">
                <TableHead className="text-[hsl(var(--lxra-muted))]">Client</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Occasion</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Blueprint</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Model</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Status</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {blueprints.length === 0 && (
                <TableRow className="border-[hsl(var(--lxra-border))]">
                  <TableCell colSpan={6} className="text-center text-[hsl(var(--lxra-muted))] py-10">
                    No blueprints yet. Share <span className="text-[hsl(var(--lxra-gold))]">/plan</span> with a client to create the first one.
                  </TableCell>
                </TableRow>
              )}
              {blueprints.map((b) => (
                <TableRow
                  key={b.id}
                  onClick={() => setSelected(b.id)}
                  data-testid={ADMIN_INTELLIGENCE.row(b.id)}
                  className={`border-[hsl(var(--lxra-border))] cursor-pointer hover:bg-[#141416] ${selected === b.id ? 'bg-[#141416]' : ''}`}
                >
                  <TableCell className="text-[hsl(var(--lxra-fg))] whitespace-nowrap">
                    <p>{b.request?.name}</p>
                    <p className="text-xs text-[hsl(var(--lxra-muted))]">{b.request?.email}</p>
                  </TableCell>
                  <TableCell className="text-[hsl(var(--lxra-fg))] max-w-xs truncate">{b.request?.occasion}</TableCell>
                  <TableCell className="text-[hsl(var(--lxra-muted))] max-w-xs truncate">{b.content?.title || '—'}</TableCell>
                  <TableCell><ModelBadge provider={b.provider} model={b.model} /></TableCell>
                  <TableCell><StatusBadge status={b.status} /></TableCell>
                  <TableCell className="text-[hsl(var(--lxra-muted))] whitespace-nowrap text-xs">{new Date(b.created_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
